/**
 * Phase 6.3 — `/studio/composer` — server-driven stepped wizard.
 *
 * Consumes the Phase 5 Studio composer endpoints:
 *   GET  /studio/sessions/composer-steps/                  -> step definitions
 *   POST /studio/sessions/{id}/composer-candidates/         -> eligible packs per step
 *   POST /studio/sessions/{id}/preview/                     -> dry-run composition (no persist)
 *
 * Step navigation uses `@radix-ui/react-progress` (accessible progress
 * bar) instead of a hand-rolled dot indicator. Workspace editor role checks
 * remain in force while the pilot Composer is open without a plan gate.
 */
import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import * as Progress from "@radix-ui/react-progress";
import {
  ArrowLeft, ArrowRight, Blocks, CheckCircle2, Download, Loader2,
  Languages, PackagePlus, ShieldCheck, ShieldAlert, Sparkles, LockKeyhole,
  BookOpen, Link2, Database, ChevronDown, ChevronUp, History, CircleHelp,
} from "lucide-react";
import { rawFetch, rawResponse } from "@/lib/api/client";
import { PageHeader, ErrorBox, EmptyState, StatusPill } from "@/components/common";
import { MicroLabel } from "@/components/DataPrimitives";
import { TierStack, type TierSegment } from "@/components/TierStack";
import { WorkspaceLandscape } from "@/components/WorkspaceLandscape";
import { WorkspaceJourney } from "@/components/WorkspaceJourney";
import { StandardAdoptionActions } from "@/components/StandardAdoptionActions";
import { useApp } from "@/lib/store";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";
import { TermSetSelector } from "@/components/TermSetSelector";

interface DependsOn { question_id: string; value: string | null }
// Track A.3 marketplace restructure: paused/planned steps carry
// per-standard advisory tiles so the wizard is honest about *why*
// a step has no buildable candidates today (planned vs. license-gated
// vs. skeleton-paused). The FE renders these instead of a single
// generic empty state.
interface ComposerStepAdvisory {
  id: string;
  name: string;
  provider: string;
  availability:
    | "planned"
    | "planned_structure_only"
    | "license_gated"
    | "skeleton_paused"
    | "draft_review_required";
  license: string;
  license_notice: string;
  description: string;
  action: "build" | "build_structure_only" | "attest_license" | "sme_review";
  drafts?: string[];
  how_to_clear?: string;
}
interface ComposerStep {
  id: string; step: number; group: string;
  prompt: string; help?: string | null;
  depends_on?: DependsOn | null;
  candidate_filter?: Record<string, unknown> | null;
  advisories?: ComposerStepAdvisory[];
  default_language?: string;
  default_language_label?: string;
  fields?: Array<{
    id: string;
    type: string;
    required?: boolean;
    choices?: string[];
    default?: string | number;
  }>;
}
interface ComposerCandidateWarning {
  code: string;
  severity: "warning" | "info";
  message: string;
}
interface PackCandidate {
  name: string; semver: string; kind: string; tier: number; universal: boolean;
  applicable_industries: string[]; jurisdiction: string; languages: string[];
  description?: string;
  status?: "DRAFT" | "PUBLISHED";
}
interface PurityViolation { universal_pack: string; dependency: string; dependency_industries: string[] }
interface PreviewResult {
  status: "PREVIEW_OK" | "PREVIEW_FAILED";
  error_stage: string; error_message: string;
  resolved_packs: { name: string; semver: string; tier: number; universal: boolean; kind: string }[];
  validation: {
    industry_purity?: { is_pure: boolean; violations: PurityViolation[] };
    shacl?: { conforms: boolean; violation_count: number };
    owl_rl?: { is_consistent: boolean; nothing_instances: number; disjoint_violations: number };
  };
  ontology_ttl: string;
  shapes_ttl: string;
  term_set_attachment?: { term_set_version_ids: string[]; digests: string[]; entity_count: number; property_count: number };
  draft_review?: {
    required: boolean;
    draft_node_count: number;
    draft_nodes: string[];
    status: string;
  };
  acceptance?: {
    source: string;
    status: {
      preview_status: string;
      release_readiness: string;
      production_approved: boolean;
    };
    counts: {
      resolved_packs: number;
      pending_actions: number;
      required_actions: number;
    };
    translations: {
      default_language: string;
      languages: string[];
    };
    required_actions: { code: string; owner: string; status: string; summary: string }[];
  };
  executive_summary_markdown?: string;
}
interface OntologyVersionSummary {
  id: string;
  semver: string;
  status: string;
}
interface MaterializedAgentSummary {
  id: string;
  name: string;
  template_id: string;
  enabled: boolean;
}
type RuntimeTarget = "triplestore_sparql" | "property_graph_cypher" | "metadata_fabric_only" | "hybrid";
interface Session {
  id: string;
  name: string;
  session_type: "architecture" | "composer";
  answers?: Record<string, unknown>;
  composer_state?: {
    step_idx?: number;
    selections?: Record<string, PackCandidate[]>;
    active_starter_slug?: string | null;
    default_language?: string;
    translation_profile_ids?: string[];
    term_set_version_ids?: string[];
    agent_template_ids?: string[];
    runtime_target?: RuntimeTarget;
    review_version_id?: string;
  };
}
interface MarketplaceStarter {
  slug: string;
  title: string;
  tagline: string;
  applicable_industries: string[];
  entities: number;
  relations: number;
  agents: number;
  status: string;
  installed?: boolean;
}
interface AgentTemplate {
  id: string;
  display_name: string;
  industry: string;
  description: string;
  default_model: string;
  default_tools: Array<{ key: string }>;
  suggested_ontology_industry: string;
  tags: string[];
  installed_count?: number;
}
interface StarterInstallResult {
  slug: string;
  counts?: Record<string, number>;
}
interface TranslationProfile {
  id: string;
  name: string;
  description: string;
  default_language: string;
  default_language_display: string;
  languages: string[];
  status: string;
  terms: unknown[];
}
type StandardAdoptionMode = "none" | "reference" | "align" | "import_subset" | "import_full";
interface StandardSource {
  id: string;
  slug: string;
  name: string;
  provider: string;
  official_url: string;
  policy_evidence_url: string;
  license_identifier: string;
  source_version: string;
  release_date: string | null;
  retrieved_at: string | null;
  syntax: string;
  owl_profile: string;
  upper_ontology: string;
  dependencies: string[];
  expected_triple_count: number | null;
  resolver_policy: string;
  transforms: string[];
  compatibility_notes: string;
  reference_permitted: boolean;
  requires_customer_license: boolean;
  rights_review_status: "DOCUMENTED" | "APPROVED" | "REJECTED" | "NEEDS_REVIEW";
  limitations: string;
  tier: 1;
  adoption_capabilities?: {
    can_reference: boolean;
    can_align_private: boolean;
    can_import_private: boolean;
    can_publish_reusable_align: boolean;
    can_publish_reusable_import: boolean;
  };
  rights_status_summary?: {
    source_rights_review_status: string;
    requires_customer_license: boolean;
    evidence_recency: string;
  };
}
interface StandardAdoption {
  id: string;
  source_slug: string;
  mode: StandardAdoptionMode;
  status: "DRAFT" | "ACTIVE" | "BLOCKED" | "EXPIRED" | "REJECTED";
  source_version: string;
  source_hash: string;
  intended_use: string;
  selected_modules: string[];
  adoption_owner: string;
  customer_rights_evidence: string;
  rights_reviewed_by: string;
  rights_reviewed_at: string | null;
  semantic_reviewed_by: string;
  semantic_reviewed_at: string | null;
  technical_compatibility_confirmed: boolean;
  acceptance_test: string;
  mapping_ttl: string;
  gate_results?: { failed_gates?: string[]; required_actions?: string[] };
  review_due_at: string | null;
  recommendation?: StandardRecommendation | null;
  promotion_status: "NONE" | "REQUESTED" | "STAFF_REVIEW" | "PUBLISHED" | "REJECTED";
}
interface RecommendationAxis {
  axis: string; score: number; weight: number; contribution: number; explanation: string;
}
interface RecommendationAlternative {
  mode: StandardAdoptionMode; permitted: boolean; selected: boolean;
  failed_gates: string[]; required_actions: string[]; reason: string;
}
interface StandardRecommendation {
  mode: StandardAdoptionMode; confidence: number; weighted_score: number;
  rationale: string; axes: RecommendationAxis[];
  alternatives: RecommendationAlternative[]; required_actions: string[];
  context?: string;
  desired_mode?: StandardAdoptionMode;
  max_permitted_mode?: StandardAdoptionMode;
  capped_by_rights?: boolean;
  capped_reason?: string;
}
interface StandardsIntent {
  interoperability_need: "none" | "low" | "medium" | "high";
  desired_mode: Exclude<StandardAdoptionMode, "none">;
  usage_context: "internal_only" | "customer_workspace" | "redistribution_candidate";
  rights_posture: "open_verified" | "customer_licensed" | "unknown";
  cq_coverage_target: number;
}
interface StandardCandidateRow {
  source: StandardSource;
  context: string;
  eligibility: {
    requested_mode: string;
    context: string;
    max_permitted_mode: StandardAdoptionMode;
    permitted: boolean;
    blockers: string[];
    required_actions: string[];
  };
  allowed_modes: StandardAdoptionMode[];
  related_packs: Array<{ name: string; semver: string; tier: number; status: string }>;
}
interface AdoptionHistoryEvent {
  id: string; action: string; actor: string; created_at: string;
  metadata: { snapshot?: { status?: string; mode?: string }; [key: string]: unknown };
}

const RUNTIME_TARGET_OPTIONS: Array<{
  value: RuntimeTarget;
  label: string;
  hint: string;
}> = [
  { value: "hybrid", label: "Hybrid", hint: "Governance artifacts plus downstream graph, API, or warehouse serving." },
  { value: "triplestore_sparql", label: "Triplestore / SPARQL", hint: "RDF-native validation and query serving." },
  { value: "property_graph_cypher", label: "Property graph / Cypher", hint: "Neo4j-style operational graph serving." },
  { value: "metadata_fabric_only", label: "Metadata fabric only", hint: "Contracts, mappings, and activation guidance without a dedicated graph runtime." },
];

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function readStandardsIntent(answers: Record<string, unknown>): StandardsIntent {
  const raw = answers.standards_intent;
  const fallback: StandardsIntent = {
    interoperability_need: "medium",
    desired_mode: "reference",
    usage_context: "customer_workspace",
    rights_posture: "unknown",
    cq_coverage_target: 60,
  };
  if (!raw || typeof raw !== "object") return fallback;
  const candidate = raw as Partial<StandardsIntent>;
  return {
    interoperability_need: candidate.interoperability_need ?? fallback.interoperability_need,
    desired_mode: candidate.desired_mode ?? fallback.desired_mode,
    usage_context: candidate.usage_context ?? fallback.usage_context,
    rights_posture: candidate.rights_posture ?? fallback.rights_posture,
    cq_coverage_target: Number(candidate.cq_coverage_target ?? fallback.cq_coverage_target),
  };
}

function stepIsGated(step: ComposerStep, answers: Record<string, unknown>): boolean {
  const dep = step.depends_on;
  if (!dep) return false;
  const value = asString(answers[dep.question_id]);
  if (dep.value == null) return !value;
  return value !== dep.value;
}

function selectedPacks(selections: Record<string, PackCandidate[]>) {
  return Object.values(selections)
    .flat()
    .reduce<{ name: string; semver: string }[]>((acc, pack) => {
      if (!acc.some((item) => item.name === pack.name && item.semver === pack.semver)) {
        acc.push({ name: pack.name, semver: pack.semver });
      }
      return acc;
    }, [])
    .sort((left, right) => `${left.name}@${left.semver}`.localeCompare(`${right.name}@${right.semver}`));
}

export function Composer() {
  const queryClient = useQueryClient();
  const ws = useApp((s) => s.workspaceId);
  const { canEdit, isLoading: roleLoading, error: roleError } = useWorkspacePermissions();
  const requestedSessionId = new URLSearchParams(window.location.search).get("session");
  const [name, setName] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(requestedSessionId);
  const [hydrated, setHydrated] = useState(!requestedSessionId);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [selections, setSelections] = useState<Record<string, PackCandidate[]>>({});
  const [activeStarterSlug, setActiveStarterSlug] = useState<string | null>(null);
  const [defaultLanguage, setDefaultLanguage] = useState("en");
  const [translationProfileIds, setTranslationProfileIds] = useState<string[]>([]);
  const [termSetVersionIds, setTermSetVersionIds] = useState<string[]>([]);
  const [agentTemplateIds, setAgentTemplateIds] = useState<string[]>([]);
  const [runtimeTarget, setRuntimeTarget] = useState<RuntimeTarget>("hybrid");
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const attemptedPreviewKey = useRef<string | null>(null);

  const sessionQ = useQuery<Session>({
    queryKey: ["composer-session", requestedSessionId],
    queryFn: () => rawFetch(`/studio/sessions/${requestedSessionId}/`),
    enabled: !!requestedSessionId,
  });

  useEffect(() => {
    const session = sessionQ.data;
    if (!session) return;
    if (session.session_type === "composer") {
      const state = session.composer_state ?? {};
      setName(session.name);
      setAnswers(session.answers ?? {});
      setStepIdx(state.step_idx ?? 0);
      setSelections(state.selections ?? {});
      setActiveStarterSlug(state.active_starter_slug ?? null);
      setDefaultLanguage(state.default_language ?? "en");
      setTranslationProfileIds(state.translation_profile_ids ?? []);
      setTermSetVersionIds(state.term_set_version_ids ?? []);
      setAgentTemplateIds(state.agent_template_ids ?? []);
      setRuntimeTarget(state.runtime_target ?? "hybrid");
    }
    setHydrated(true);
  }, [sessionQ.data]);

  useEffect(() => {
    if (!canEdit || !hydrated || !sessionId) return;
    const timer = window.setTimeout(() => {
      rawFetch(`/studio/sessions/${sessionId}/`, {
        method: "PATCH",
        body: JSON.stringify({
          answers,
          composer_state: {
            ...(sessionQ.data?.composer_state ?? {}),
            step_idx: stepIdx,
            selections,
            pack_selections: selectedPacks(selections),
            active_starter_slug: activeStarterSlug,
            default_language: defaultLanguage,
            translation_profile_ids: translationProfileIds,
            term_set_version_ids: termSetVersionIds,
            agent_template_ids: agentTemplateIds,
            runtime_target: runtimeTarget,
          },
        }),
      }).catch(() => undefined);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [activeStarterSlug, agentTemplateIds, answers, canEdit, defaultLanguage, hydrated, runtimeTarget, selections, sessionId, sessionQ.data?.composer_state, stepIdx, translationProfileIds, termSetVersionIds]);

  const stepsQ = useQuery<{ steps: ComposerStep[] }>({
    queryKey: ["composer-steps"],
    queryFn: () => rawFetch(`/studio/sessions/composer-steps/`),
  });
  const steps = stepsQ.data?.steps ?? [];
  const step = steps[stepIdx];
  const isPreviewStep = step?.candidate_filter == null && step?.id === "preview";
  const gated = step ? stepIsGated(step, answers) : false;

  const start = useMutation({
    mutationFn: () => rawFetch<Session>(`/studio/sessions/`, {
      method: "POST",
      body: JSON.stringify({ workspace: ws, name: name || "Composer session", session_type: "composer" }),
    }),
    onSuccess: (session) => {
      setSessionId(session.id);
      setHydrated(true);
      window.history.replaceState({}, "", `/studio/composer?session=${session.id}`);
    },
  });

  const candidatesQ = useQuery<{ step: string; candidates: PackCandidate[]; warnings?: ComposerCandidateWarning[] }>({
    queryKey: ["composer-candidates", sessionId, step?.id, answers],
    queryFn: () => rawFetch(`/studio/sessions/${sessionId}/composer-candidates/`, {
      method: "POST",
      body: JSON.stringify({ step: step!.id, answers }),
    }),
    enabled: canEdit && !!sessionId && !!step && !isPreviewStep && !gated,
  });

  const startersQ = useQuery<{ results: MarketplaceStarter[] }>({
    queryKey: ["marketplace-templates", ws],
    queryFn: () => rawFetch(`/packs/marketplace/templates/?workspace_id=${ws}`),
    enabled: !!sessionId && step?.id === "industry",
  });

  const translationProfilesQ = useQuery<{ results: TranslationProfile[] }>({
    queryKey: ["translation-profiles", ws, "approved"],
    queryFn: () => rawFetch(
      `/ontology/translation-profiles/?workspace=${ws}&status=approved&page_size=100`,
    ),
    enabled: !!sessionId && step?.id === "languages",
  });

  const agentTemplatesQ = useQuery<{ templates: AgentTemplate[] }>({
    queryKey: ["agent-templates", ws],
    queryFn: () => rawFetch(`/agents/templates/?workspace=${ws}`),
    enabled: !!sessionId && isPreviewStep,
  });

  const standardSourcesQ = useQuery<{ results: StandardSource[] }>({
    queryKey: ["standard-sources"],
    queryFn: () => rawFetch(`/composition/standard-sources/?page_size=100`),
    enabled: !!sessionId && step?.id === "standards",
  });

  const standardAdoptionsQ = useQuery<{ results: StandardAdoption[] }>({
    queryKey: ["standard-adoptions", ws],
    queryFn: () => rawFetch(`/composition/standard-adoptions/?page_size=100`),
    enabled: !!sessionId && step?.id === "standards",
  });

  const standardsIntent = readStandardsIntent(answers);
  const standardCandidatesQ = useQuery<{ results: StandardCandidateRow[] }>({
    queryKey: ["standard-candidates", ws, standardsIntent],
    queryFn: () => rawFetch(
      `/composition/standards/candidates/?workspace=${ws}&standards_intent=${encodeURIComponent(JSON.stringify(standardsIntent))}`,
    ),
    enabled: !!sessionId && step?.id === "standards",
  });

  const saveStandardAdoption = useMutation<StandardAdoption, Error, {
    sourceSlug: string;
    mode: StandardAdoptionMode;
  }>({
    mutationFn: ({ sourceSlug, mode }) => {
      const current = standardAdoptionsQ.data?.results.find(
        (adoption) => adoption.source_slug === sourceSlug,
      );
      return rawFetch(
        current
          ? `/composition/standard-adoptions/${current.id}/`
          : `/composition/standard-adoptions/`,
        {
          method: current ? "PATCH" : "POST",
          body: JSON.stringify({ source_slug: sourceSlug, mode }),
        },
      );
    },
    onSuccess: () => standardAdoptionsQ.refetch(),
  });

  const recommendStandardAdoption = useMutation<StandardRecommendation, Error, {
    adoptionId: string;
    scores: Record<string, number>;
    semanticMappingRequired: boolean;
    broadSemanticsRequired: boolean;
    standardsIntent: StandardsIntent;
  }>({
    mutationFn: ({ adoptionId, scores, semanticMappingRequired, broadSemanticsRequired, standardsIntent }) => rawFetch(
      `/composition/standard-adoptions/${adoptionId}/recommend/`,
      {
        method: "POST",
        body: JSON.stringify({
          scores,
          semantic_mapping_required: semanticMappingRequired,
          broad_semantics_required: broadSemanticsRequired,
          desired_mode: standardsIntent.desired_mode,
          usage_context: standardsIntent.usage_context,
          rights_posture: standardsIntent.rights_posture,
        }),
      },
    ),
    onSuccess: (_, variables) => {
      standardAdoptionsQ.refetch();
      queryClient.invalidateQueries({
        queryKey: ["standard-adoption-history", variables.adoptionId],
      });
    },
  });

  useEffect(() => {
    if (step?.id === "languages" && step.default_language) {
      setDefaultLanguage((current) => current || step.default_language!);
    }
  }, [step?.id, step?.default_language]);

  useEffect(() => {
    setAnswers((current) => ({
      ...current,
      languages: defaultLanguage,
      translation_profiles: translationProfileIds.join(","),
    }));
  }, [defaultLanguage, translationProfileIds]);

  const installStarter = useMutation<StarterInstallResult, Error, string>({
    mutationFn: (slug) => rawFetch(`/packs/marketplace/templates/${slug}/install/`, {
      method: "POST",
      body: JSON.stringify({ workspace_id: ws }),
    }),
    onSuccess: (_, slug) => {
      const starter = startersQ.data?.results.find((item) => item.slug === slug);
      setActiveStarterSlug(slug);
      setAnswers((current) => ({
        ...current,
        industry: starter?.applicable_industries[0] ?? "",
      }));
      setSelections((current) => ({ ...current, industry: [] }));
      startersQ.refetch();
    },
  });

  const activeStarter = startersQ.data?.results.find(
    (starter) => starter.slug === activeStarterSlug && starter.installed,
  );

  const useStarter = (starter: MarketplaceStarter) => {
    setActiveStarterSlug(starter.slug);
    setAnswers((current) => ({
      ...current,
      industry: starter.applicable_industries[0] ?? "",
    }));
    setSelections((current) => ({ ...current, industry: [] }));
  };

  const previewMutation = useMutation({
    mutationFn: () => {
      const pack_selections = selectedPacks(selections);
      return rawFetch<PreviewResult>(`/studio/sessions/${sessionId}/preview/`, {
        method: "POST",
        body: JSON.stringify({
          pack_selections,
          default_language: defaultLanguage,
          translation_profiles: translationProfileIds,
          term_set_version_ids: termSetVersionIds,
        }),
      });
    },
    onSuccess: (r) => setPreview(r),
  });

  // Run the preview automatically the moment the user reaches the last step.
  useEffect(() => {
    const packs = selectedPacks(selections);
    const previewKey = sessionId && packs.length > 0
      ? `${sessionId}:${JSON.stringify(packs)}:${JSON.stringify(termSetVersionIds)}`
      : null;
    if (canEdit && isPreviewStep && previewKey && attemptedPreviewKey.current !== previewKey) {
      attemptedPreviewKey.current = previewKey;
      previewMutation.mutate();
    }
  }, [canEdit, isPreviewStep, sessionId, selections, termSetVersionIds, previewMutation.mutate]);

  const toggleCandidate = (c: PackCandidate) => {
    if (!step) return;
    if (step.id === "industry") setActiveStarterSlug(null);
    attemptedPreviewKey.current = null;
    setPreview(null);
    setSelections((prev) => {
      const current = prev[step.id] ?? [];
      const exists = current.some((x) => x.name === c.name && x.semver === c.semver);
      if (exists) {
        return {
          ...prev,
          [step.id]: current.filter(
            (x) => !(x.name === c.name && x.semver === c.semver),
          ),
        };
      }
      if (step.id !== "industry") {
        return { ...prev, [step.id]: [...current, c] };
      }

      const industries = new Set(c.applicable_industries ?? []);
      const compatible = Object.fromEntries(
        Object.entries(prev).map(([stepId, packs]) => [
          stepId,
          packs.filter((pack) => (
            pack.universal
            || pack.applicable_industries.includes("*")
            || pack.applicable_industries.some((industry) => industries.has(industry))
          )),
        ]),
      );
      return {
        ...compatible,
        industry: [...(compatible.industry ?? []), c],
      };
    });
    // The wizard's later steps (regulations/extensions) narrow by the
    // industry the user picked on this step.
    if (step.id === "industry" && c.applicable_industries?.length) {
      setAnswers((a) => ({ ...a, industry: c.applicable_industries[0] }));
    }
  };

  const selectedForStep = step ? selections[step.id] ?? [] : [];
  const progressPct = steps.length ? Math.round(((stepIdx + 1) / steps.length) * 100) : 0;

  if (!ws) {
    return (
      <>
        <PageHeader title="Composer" subtitle="Server-driven wizard to compose an ontology from marketplace packs." />
        <EmptyState title="Select a workspace first." hint="Use the workspace switcher in the header." />
      </>
    );
  }

  if (!canEdit) {
    return <>
      <PageHeader title="Enterprise Semantic Composer"
                  subtitle="Compose governed ontology releases from approved semantic assets." />
      <div className="card text-sm text-muted" role="status">
        {roleLoading ? "Checking your workspace role…" : roleError ?
          "Unable to verify your workspace role. Editing is unavailable." :
          "Composer authoring requires a workspace editor or admin. You can inspect published ontologies in the Ontology page."}
      </div>
    </>;
  }

  return (
    <>
      <PageHeader
        title="Enterprise Semantic Composer"
        subtitle="Compose governed ontology releases from approved, reusable semantic assets."
        help="Each step narrows server-side pack candidates from your answers and saves progress to this Studio session. The final step validates the composition, exports a review scaffold, or creates an editable SME review draft; publishing remains separate."
      />

      <WorkspaceJourney compact />

      <div className="mb-4 grid border-y border-border bg-panel/40 sm:grid-cols-3">
        <div className="flex items-center gap-3 px-4 py-3"><ShieldCheck size={18} className="text-good" /><div><div className="text-xs font-semibold">Validation gates</div><div className="text-[11px] text-muted">SHACL, OWL-RL and purity</div></div></div>
        <div className="flex items-center gap-3 border-border px-4 py-3 sm:border-x"><LockKeyhole size={18} className="text-accent" /><div><div className="text-xs font-semibold">Governed assets</div><div className="text-[11px] text-muted">Versioned and provenance-ready</div></div></div>
        <div className="flex items-center gap-3 px-4 py-3"><PackagePlus size={18} className="text-accent" /><div><div className="text-xs font-semibold">Deployment package</div><div className="text-[11px] text-muted">Complete enterprise scaffold</div></div></div>
      </div>

      <div className="mb-4"><WorkspaceLandscape workspace={ws} termSetVersionIds={termSetVersionIds} /></div>

      <div className="mb-4">
        <TermSetSelector workspace={ws} selected={termSetVersionIds} onChange={(ids) => { setTermSetVersionIds(ids); setPreview(null); attemptedPreviewKey.current = null; }} disabled={!canEdit} />
      </div>

      <div>
        {sessionQ.isError ? (
          <ErrorBox error={sessionQ.error} />
        ) : !hydrated ? (
          <div className="text-sm text-muted"><Loader2 className="mr-1 inline animate-spin" size={14} /> Restoring Composer session…</div>
        ) : sessionQ.data && sessionQ.data.session_type !== "composer" ? (
          <ErrorBox error={new Error("This Studio session is an architecture interview, not a Composer session.")} />
        ) : !sessionId ? (
          <div className="card max-w-md">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
              Start a composer session
            </div>
            <input
              className="input mb-2" placeholder="Name (e.g. Acme EU expansion)"
              value={name} onChange={(e) => setName(e.target.value)}
            />
            <button className="btn-primary w-full" disabled={start.isPending} onClick={() => start.mutate()}>
              {start.isPending ? <Loader2 className="mr-1 inline animate-spin" size={14} /> : <Blocks className="mr-1 inline" size={14} />}
              Start composing
            </button>
            {start.isError && <div className="mt-2"><ErrorBox error={start.error} /></div>}
          </div>
        ) : stepsQ.isLoading || !step ? (
          <div className="text-sm text-muted">Loading steps…</div>
        ) : (
          <div className="space-y-4">
            <div className="card">
              <div className="mb-2 flex items-center justify-between text-xs text-muted">
                <span>Step {stepIdx + 1} of {steps.length}</span>
                <span className="uppercase tracking-wide">{step.group}</span>
              </div>
              <Progress.Root
                value={progressPct}
                className="h-1.5 w-full overflow-hidden rounded-full bg-border"
              >
                <Progress.Indicator
                  className="h-full bg-accent transition-transform"
                  style={{ transform: `translateX(-${100 - progressPct}%)` }}
                />
              </Progress.Root>
            </div>

            <div className="card">
              <h2 className="text-lg font-semibold">{step.prompt}</h2>
              {step.help && <p className="mt-1 text-sm text-muted">{step.help}</p>}

              {step.id === "industry" && (
                <MarketplaceStarterPicker
                  starters={startersQ.data?.results ?? []}
                  loading={startersQ.isLoading}
                  error={startersQ.error ?? installStarter.error}
                  installing={installStarter.variables}
                  activeSlug={activeStarterSlug}
                  onInstall={(slug) => installStarter.mutate(slug)}
                  onUse={useStarter}
                />
              )}

              {isPreviewStep ? (
                <>
                <PreviewPanel
                  result={preview}
                  pending={previewMutation.isPending}
                  error={previewMutation.error}
                  sessionId={sessionId}
                  selections={selections}
                  bundleName={name}
                  installedStarter={activeStarter?.title}
                  defaultLanguage={defaultLanguage}
                  translationProfileIds={translationProfileIds}
                  termSetVersionIds={termSetVersionIds}
                  runtimeTarget={runtimeTarget}
                  onRuntimeTarget={setRuntimeTarget}
                  existingReviewVersionId={sessionQ.data?.composer_state?.review_version_id}
                  agentTemplates={recommendedAgentTemplates(agentTemplatesQ.data?.templates ?? [], asString(answers.industry))}
                  selectedAgentTemplateIds={agentTemplateIds}
                  onToggleAgentTemplate={(templateId) => setAgentTemplateIds((current) => (
                    current.includes(templateId)
                      ? current.filter((id) => id !== templateId)
                      : [...current, templateId]
                  ))}
                />
                </>
              ) : gated ? (
                <EmptyState title="Answer the previous step first." />
              ) : step.id === "languages" ? (
                <LanguageProfilePicker
                  profiles={translationProfilesQ.data?.results ?? []}
                  loading={translationProfilesQ.isLoading}
                  error={translationProfilesQ.error}
                  defaultLanguage={defaultLanguage}
                  defaultLabel={step.default_language_label}
                  selectedProfileIds={translationProfileIds}
                  onDefaultLanguage={setDefaultLanguage}
                  onToggleProfile={(profileId) => setTranslationProfileIds((current) => (
                    current.includes(profileId)
                      ? current.filter((id) => id !== profileId)
                      : [...current, profileId]
                  ))}
                />
              ) : step.id === "standards_intent" ? (
                <StandardsIntentPanel
                  value={standardsIntent}
                  onChange={(next) => setAnswers((current) => ({ ...current, standards_intent: next }))}
                />
              ) : step.id === "standards" ? (
                <StandardAdoptionPanel
                  sources={standardSourcesQ.data?.results ?? []}
                  adoptions={standardAdoptionsQ.data?.results ?? []}
                  standardsCandidates={standardCandidatesQ.data?.results ?? []}
                  candidateWarnings={candidatesQ.data?.warnings ?? []}
                  standardsIntent={standardsIntent}
                  loading={standardSourcesQ.isLoading || standardAdoptionsQ.isLoading || standardCandidatesQ.isLoading}
                  error={standardSourcesQ.error ?? standardAdoptionsQ.error ?? standardCandidatesQ.error ?? saveStandardAdoption.error ?? recommendStandardAdoption.error}
                  savingSource={saveStandardAdoption.isPending ? saveStandardAdoption.variables?.sourceSlug : undefined}
                  recommendingAdoption={recommendStandardAdoption.isPending ? recommendStandardAdoption.variables?.adoptionId : undefined}
                  onMode={(sourceSlug, mode) => saveStandardAdoption.mutate({ sourceSlug, mode })}
                  onRecommend={(adoptionId, scores, semanticMappingRequired, broadSemanticsRequired) => (
                    recommendStandardAdoption.mutate({
                      adoptionId, scores, semanticMappingRequired, broadSemanticsRequired, standardsIntent,
                    })
                  )}
                  onChanged={(adoptionId) => {
                    standardAdoptionsQ.refetch();
                    queryClient.invalidateQueries({
                      queryKey: ["standard-adoption-history", adoptionId],
                    });
                  }}
                />
              ) : candidatesQ.isLoading ? (
                <div className="mt-4 text-sm text-muted"><Loader2 className="mr-1 inline animate-spin" size={14} /> Loading candidates…</div>
              ) : candidatesQ.isError ? (
                <div className="mt-4"><ErrorBox error={candidatesQ.error} /></div>
              ) : (candidatesQ.data?.candidates.length ?? 0) === 0 ? (
                <>
                  {step.id === "industry" ? null : step.advisories && step.advisories.length > 0 ? (
                    <AdvisoryTiles advisories={step.advisories} />
                  ) : (
                    <div className="mt-4">
                      <EmptyState
                        title="No composable packs are published for this step."
                        hint="Continue with Next; this optional layer can be added when a validated pack is published."
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  {step.id === "industry" && (
                    <div className="mb-3 mt-5 text-xs font-semibold uppercase tracking-wide text-muted">
                      Or compose from published industry packs
                    </div>
                  )}
                  <ul className={`${step.id === "industry" ? "" : "mt-4"} grid gap-3 sm:grid-cols-2 lg:grid-cols-3`}>
                    {candidatesQ.data!.candidates.map((c) => {
                    const selected = selectedForStep.some((x) => x.name === c.name && x.semver === c.semver);
                    return (
                      <li key={`${c.name}@${c.semver}`}>
                        <button
                          type="button"
                          onClick={() => toggleCandidate(c)}
                          className={`flex h-full w-full flex-col items-start gap-2 rounded-[7px] border p-3 text-left text-xs transition-colors ${
                            selected
                              ? "border-accent bg-accent/10"
                              : "border-border bg-panel/60 hover:border-accent/40 hover:bg-accent/5"
                          }`}
                        >
                          <div className="flex w-full items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="truncate text-[13px] font-semibold text-text">{c.name}</div>
                              <div className="mt-0.5 font-mono text-[10px] text-muted">v{c.semver}</div>
                            </div>
                            {selected && <CheckCircle2 size={16} className="shrink-0 text-accent" />}
                          </div>
                          {c.description && (
                            <div className="line-clamp-2 text-[11.5px] leading-snug text-muted">
                              {c.description}
                            </div>
                          )}
                          <div className="mt-auto flex flex-wrap gap-1">
                            <span className="rounded-[5px] border border-border bg-bg/50 px-1.5 py-[1px] font-mono text-[10px] uppercase tracking-[0.03em] text-muted">
                              {c.kind || "\u2014"}
                            </span>
                            <span className="rounded-[5px] border border-border bg-bg/50 px-1.5 py-[1px] font-mono text-[10px] uppercase tracking-[0.03em] text-muted">
                              T{c.tier}
                            </span>
                            {c.universal && (
                              <span className="rounded-[5px] border border-good/30 bg-good/10 px-1.5 py-[1px] font-mono text-[10px] uppercase tracking-[0.03em] text-good">
                                universal
                              </span>
                            )}
                            {c.status === "DRAFT" && (
                              <span className="rounded-[5px] border border-warn/30 bg-warn/10 px-1.5 py-[1px] font-mono text-[10px] uppercase tracking-[0.03em] text-warn">
                                authored draft
                              </span>
                            )}
                            {c.applicable_industries?.slice(0, 2).map((ind) => (
                              <span
                                key={ind}
                                className="rounded-[5px] border border-accent/30 bg-accent/10 px-1.5 py-[1px] font-mono text-[10px] uppercase tracking-[0.03em] text-accent"
                              >
                                {ind}
                              </span>
                            ))}
                          </div>
                        </button>
                      </li>
                    );
                    })}
                  </ul>
                </>
              )}
            </div>

            <div className="flex items-center justify-between">
              <button
                className="btn" disabled={stepIdx === 0}
                onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
              >
                <ArrowLeft size={14} className="mr-1 inline" /> Back
              </button>
              {stepIdx < steps.length - 1 && (
                <button
                  className="btn-primary"
                  onClick={() => setStepIdx((i) => Math.min(steps.length - 1, i + 1))}
                >
                  Next <ArrowRight size={14} className="ml-1 inline" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const STANDARD_MODES: Array<{
  value: StandardAdoptionMode;
  label: string;
  description: string;
}> = [
  { value: "none", label: "Not adopted", description: "Keep this source outside the composition." },
  { value: "reference", label: "Reference", description: "Citation metadata only, such as dcterms:references. No semantic claim." },
  { value: "align", label: "Private align", description: "Any SKOS, RDFS, or OWL mapping. Private to this workspace pending semantic and rights review." },
  { value: "import_subset", label: "Import subset", description: "Selected source modules, with rights, technical compatibility, and acceptance gates." },
  { value: "import_full", label: "Import full", description: "Full source import, with rights, technical compatibility, and acceptance gates." },
];

function StandardsIntentPanel({ value, onChange }: {
  value: StandardsIntent;
  onChange: (next: StandardsIntent) => void;
}) {
  return (
    <div className="mt-4 grid gap-3 rounded-[7px] border border-border bg-panel/50 p-3 sm:grid-cols-2">
      <label className="text-[11px] text-muted">Interoperability need
        <select className="input mt-1 w-full" value={value.interoperability_need} onChange={(event) => onChange({ ...value, interoperability_need: event.target.value as StandardsIntent["interoperability_need"] })}>
          <option value="none">None</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      </label>
      <label className="text-[11px] text-muted">Desired mode
        <select className="input mt-1 w-full" value={value.desired_mode} onChange={(event) => onChange({ ...value, desired_mode: event.target.value as StandardsIntent["desired_mode"] })}>
          <option value="reference">Reference</option>
          <option value="align">Align</option>
          <option value="import_subset">Import subset</option>
          <option value="import_full">Import full</option>
        </select>
      </label>
      <label className="text-[11px] text-muted">Usage context
        <select className="input mt-1 w-full" value={value.usage_context} onChange={(event) => onChange({ ...value, usage_context: event.target.value as StandardsIntent["usage_context"] })}>
          <option value="internal_only">Internal only</option>
          <option value="customer_workspace">Customer workspace</option>
          <option value="redistribution_candidate">Redistribution candidate</option>
        </select>
      </label>
      <label className="text-[11px] text-muted">Rights posture
        <select className="input mt-1 w-full" value={value.rights_posture} onChange={(event) => onChange({ ...value, rights_posture: event.target.value as StandardsIntent["rights_posture"] })}>
          <option value="open_verified">Open verified</option>
          <option value="customer_licensed">Customer licensed</option>
          <option value="unknown">Unknown</option>
        </select>
      </label>
      <label className="text-[11px] text-muted sm:col-span-2">CQ coverage target (%)
        <input className="input mt-1 w-full" type="number" min="0" max="100" step="1" value={value.cq_coverage_target}
          onChange={(event) => onChange({ ...value, cq_coverage_target: Number(event.target.value) })}
        />
      </label>
    </div>
  );
}

function StandardAdoptionPanel({
  sources, adoptions, standardsCandidates, candidateWarnings, standardsIntent,
  loading, error, savingSource, recommendingAdoption, onMode, onRecommend, onChanged,
}: {
  sources: StandardSource[];
  adoptions: StandardAdoption[];
  standardsCandidates: StandardCandidateRow[];
  candidateWarnings: ComposerCandidateWarning[];
  standardsIntent: StandardsIntent;
  loading: boolean;
  error: unknown;
  savingSource?: string;
  recommendingAdoption?: string;
  onMode: (sourceSlug: string, mode: StandardAdoptionMode) => void;
  onRecommend: (
    adoptionId: string, scores: Record<string, number>,
    semanticMappingRequired: boolean, broadSemanticsRequired: boolean,
  ) => void;
  onChanged: (adoptionId: string) => void;
}) {
  if (loading) {
    return <div className="mt-4 text-sm text-muted"><Loader2 className="mr-1 inline animate-spin" size={14} /> Loading standard sources…</div>;
  }
  if (error) return <div className="mt-4"><ErrorBox error={error} /></div>;

  return (
    <div className="mt-4 space-y-4">
      <div className="grid border-y border-border bg-bg/30 sm:grid-cols-3">
        <div className="flex gap-2 px-3 py-3">
          <BookOpen size={16} className="mt-0.5 shrink-0 text-accent" />
          <div><div className="text-xs font-semibold">Reference</div><div className="text-[11px] text-muted">Non-logical citation only</div></div>
        </div>
        <div className="flex gap-2 border-border px-3 py-3 sm:border-x">
          <Link2 size={16} className="mt-0.5 shrink-0 text-warn" />
          <div><div className="text-xs font-semibold">Align</div><div className="text-[11px] text-muted">All semantic mappings stay private</div></div>
        </div>
        <div className="flex gap-2 px-3 py-3">
          <Database size={16} className="mt-0.5 shrink-0 text-good" />
          <div><div className="text-xs font-semibold">Import</div><div className="text-[11px] text-muted">Source content plus technical gates</div></div>
        </div>
      </div>

      {sources.length === 0 ? (
        <EmptyState title="No active standard sources are registered." />
      ) : (
        <ul className="divide-y divide-border border-y border-border">
          {candidateWarnings.length > 0 && (
            <li className="bg-warn/10 px-3 py-3 text-xs text-warn">
              {candidateWarnings.map((warning) => (
                <div key={warning.code}>{warning.message}</div>
              ))}
            </li>
          )}
          {sources.map((source) => (
            <StandardSourceRow
              key={source.slug}
              source={source}
              adoption={adoptions.find((item) => item.source_slug === source.slug)}
              candidate={standardsCandidates.find((item) => item.source.slug === source.slug)}
              standardsIntent={standardsIntent}
              saving={savingSource === source.slug}
              recommending={recommendingAdoption === adoptions.find((item) => item.source_slug === source.slug)?.id}
              onMode={onMode}
              onRecommend={onRecommend}
              onChanged={() => {
                const adoption = adoptions.find((item) => item.source_slug === source.slug);
                if (adoption) onChanged(adoption.id);
              }}
            />
          ))}
        </ul>
      )}
      <p className="text-[11px] leading-relaxed text-muted">
        Promotion is not an adoption mode. A reviewed private alignment can be submitted separately for reusable publication only after provenance, mapping and derivative rights, customer export rights, and redistribution rights are each affirmatively confirmed; staff publishing rechecks those rights.
      </p>
    </div>
  );
}

const RECOMMENDATION_AXES: Array<{ key: string; label: string }> = [
  { key: "required_slice_coverage", label: "Required scope coverage" },
  { key: "external_interoperability_value", label: "External interoperability" },
  { key: "identifier_value", label: "Identifiers and codes" },
  { key: "governance_health", label: "Governance health" },
  { key: "semantic_technical_fit", label: "Semantic and technical fit" },
  { key: "lifecycle_cost", label: "Lifecycle supportability" },
  { key: "local_extension_burden", label: "Local extension reduction" },
];

function StandardSourceRow({ source, adoption, candidate, standardsIntent, saving, recommending, onMode, onRecommend, onChanged }: {
  source: StandardSource;
  adoption?: StandardAdoption;
  candidate?: StandardCandidateRow;
  standardsIntent: StandardsIntent;
  saving: boolean;
  recommending: boolean;
  onMode: (sourceSlug: string, mode: StandardAdoptionMode) => void;
  onRecommend: (adoptionId: string, scores: Record<string, number>, semantic: boolean, broad: boolean) => void;
  onChanged: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [scores, setScores] = useState<Record<string, number>>(
    Object.fromEntries(RECOMMENDATION_AXES.map(({ key }) => [key, 5])),
  );
  const [semanticMappingRequired, setSemanticMappingRequired] = useState(false);
  const [broadSemanticsRequired, setBroadSemanticsRequired] = useState(false);
  const mode = adoption?.mode ?? "none";
  const failedGates = adoption?.gate_results?.failed_gates ?? [];
  const capability = source.adoption_capabilities;
  const policyCap = candidate?.eligibility?.max_permitted_mode;
  const capBlockers = candidate?.eligibility?.blockers ?? [];
  const historyQ = useQuery<{ events: AdoptionHistoryEvent[] }>({
    queryKey: ["standard-adoption-history", adoption?.id],
    queryFn: () => rawFetch(`/composition/standard-adoptions/${adoption!.id}/history/`),
    enabled: expanded && !!adoption,
  });

  return (
    <li className="bg-panel/40 px-3 py-4">
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.9fr)]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-text">{source.slug === "acord" ? "ACORD — Reference only: bring your own licence" : source.name}</span>
            <span className="pill">Tier {source.tier}</span>
            <span className="pill">{source.license_identifier || "Contract-specific"}</span>
            <StatusPill status={source.rights_review_status.toLowerCase()} />
          </div>
          <div className="mt-1 text-[11px] text-muted">{source.provider}</div>
          {source.limitations && <p className="mt-2 text-xs leading-relaxed text-muted">{source.limitations}</p>}
          {source.slug === "fibo" && <p className="mt-2 text-xs text-muted">Alignment is limited to the pinned BE, FND and FBC modules. Record the exact release and artifact hash in the rights review. The MIT notice travels with an ORB export.</p>}
          {source.slug === "acord" && <p className="mt-2 text-xs text-muted">No ACORD ontology is bundled. A licensed customer artifact can be imported privately in Ontology → Bring your own ontology, subject to your contract and review.</p>}
          <div className="mt-2 flex flex-wrap gap-3 text-[11px]">
            <a className="text-accent hover:underline" href={source.official_url} target="_blank" rel="noreferrer">Official source</a>
            <a className="text-accent hover:underline" href={source.policy_evidence_url} target="_blank" rel="noreferrer">Rights evidence</a>
          </div>
          {capability && (
            <div className="mt-2 flex flex-wrap gap-1 text-[10px]">
              {capability.can_reference && <span className="pill">reference allowed</span>}
              {capability.can_align_private && <span className="pill">private align</span>}
              {capability.can_import_private && <span className="pill">private import</span>}
              {capability.can_publish_reusable_align && <span className="pill">reusable align</span>}
              {capability.can_publish_reusable_import && <span className="pill">reusable import</span>}
            </div>
          )}
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold">Adoption behavior</label>
          <select className="input w-full" value={mode} disabled={saving}
            onChange={(event) => onMode(source.slug, event.target.value as StandardAdoptionMode)}>
            {STANDARD_MODES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <p className="mt-1.5 text-[11px] leading-relaxed text-muted">{STANDARD_MODES.find((option) => option.value === mode)?.description}</p>
          {saving ? (
            <div className="mt-2 text-[11px] text-muted"><Loader2 size={12} className="mr-1 inline animate-spin" /> Saving decision…</div>
          ) : adoption && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
              <StatusPill status={adoption.status.toLowerCase()} />
              {failedGates.length > 0 && <span className="text-warn">{failedGates.length} gate{failedGates.length === 1 ? "" : "s"} pending</span>}
              {adoption.review_due_at && <span className="pill">Review {new Date(adoption.review_due_at).toLocaleDateString()}</span>}
              {adoption.promotion_status !== "NONE" && <span className="pill">Promotion: {adoption.promotion_status.replaceAll("_", " ").toLowerCase()}</span>}
            </div>
          )}
          {policyCap && (
            <div className="mt-2 text-[11px] text-muted">
              Context cap ({standardsIntent.usage_context.replaceAll("_", " ")}): {policyCap}
            </div>
          )}
          {capBlockers.length > 0 && (
            <div className="mt-1 text-[11px] text-warn">Policy blockers: {capBlockers.join(", ")}</div>
          )}
        </div>
      </div>
      <button type="button" className="mt-3 flex items-center gap-1 text-[11px] font-semibold text-accent" onClick={() => setExpanded((value) => !value)}>
        {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Compatibility, recommendation and history
      </button>
      {expanded && (
        <div className="mt-3 grid gap-4 border-t border-border pt-3 lg:grid-cols-2">
          <div>
            <div className="mb-2 text-xs font-semibold">Compatibility record</div>
            <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-[11px]">
              <dt className="text-muted">Release</dt><dd>{source.source_version || "Not pinned"}{source.release_date ? ` (${source.release_date})` : ""}</dd>
              <dt className="text-muted">Retrieved</dt><dd>{source.retrieved_at || "Not recorded"}</dd>
              <dt className="text-muted">Syntax/profile</dt><dd>{[source.syntax, source.owl_profile].filter(Boolean).join(" / ") || "Not recorded"}</dd>
              <dt className="text-muted">Resolver</dt><dd>{source.resolver_policy || "Not approved"}</dd>
              <dt className="text-muted">Dependencies</dt><dd>{source.dependencies?.join(", ") || "None recorded"}</dd>
              <dt className="text-muted">Expected size</dt><dd>{source.expected_triple_count?.toLocaleString() ?? "Not recorded"}</dd>
              <dt className="text-muted">Transforms</dt><dd>{source.transforms?.join(", ") || "None approved"}</dd>
            </dl>
            {source.compatibility_notes && <p className="mt-2 text-[11px] leading-relaxed text-muted">{source.compatibility_notes}</p>}
            {failedGates.length > 0 && <div className="mt-3 text-[11px] text-warn">Failed gates: {failedGates.join(", ")}</div>}
          </div>
          <div>
            <div className="mb-2 text-xs font-semibold">Gate-first recommendation</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {RECOMMENDATION_AXES.map(({ key, label }) => (
                <label key={key} className="text-[11px] text-muted">{label}
                  <input className="input mt-1 w-full" type="number" min="0" max="10" step="1" value={scores[key]}
                    onChange={(event) => setScores((current) => ({ ...current, [key]: Number(event.target.value) }))} />
                </label>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-[11px]">
              <label><input type="checkbox" checked={semanticMappingRequired} onChange={(event) => setSemanticMappingRequired(event.target.checked)} /> Semantic mapping required</label>
              <label><input type="checkbox" checked={broadSemanticsRequired} onChange={(event) => setBroadSemanticsRequired(event.target.checked)} /> Broad native semantics required</label>
            </div>
            <button className="btn mt-3" disabled={!adoption || recommending} onClick={() => adoption && onRecommend(adoption.id, scores, semanticMappingRequired, broadSemanticsRequired)}>
              {recommending ? <Loader2 size={13} className="mr-1 inline animate-spin" /> : <Sparkles size={13} className="mr-1 inline" />} Recommend mode
            </button>
            {!adoption && <p className="mt-1 text-[11px] text-muted">Save an adoption behavior before requesting a recommendation.</p>}
            {adoption?.recommendation && <RecommendationResult result={adoption.recommendation} />}
          </div>
          {adoption && (
            <div className="lg:col-span-2">
              <StandardAdoptionActions adoption={adoption} sourceSlug={source.slug} onChanged={onChanged} />
            </div>
          )}
          {adoption && (
            <div className="lg:col-span-2">
              <div className="mb-2 flex items-center gap-1 text-xs font-semibold"><History size={13} /> Lifecycle history</div>
              {historyQ.isLoading ? <div className="text-[11px] text-muted">Loading history…</div> : historyQ.isError ? <ErrorBox error={historyQ.error} /> : (
                <ol className="divide-y divide-border border-y border-border text-[11px]">
                  {(historyQ.data?.events ?? []).map((event) => <li key={event.id} className="flex flex-wrap justify-between gap-2 py-2"><span>{event.action.replace("standard_adoption.", "").replaceAll("_", " ")}</span><span className="text-muted">{event.actor} · {new Date(event.created_at).toLocaleString()}</span></li>)}
                </ol>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function RecommendationResult({ result }: { result: StandardRecommendation }) {
  return (
    <div className="mt-3 border-l-2 border-accent pl-3 text-[11px]">
      <div className="font-semibold">Recommended: {STANDARD_MODES.find(({ value }) => value === result.mode)?.label} · {result.weighted_score.toFixed(2)}/10</div>
      <p className="mt-1 text-muted">{result.rationale}</p>
      {result.max_permitted_mode && (
        <div className="mt-1 text-muted">
          Max permitted mode: {result.max_permitted_mode}
          {result.context ? ` (${result.context.replaceAll("_", " ")})` : ""}
        </div>
      )}
      {result.capped_by_rights && (
        <div className="mt-1 text-warn">Policy cap applied: {result.capped_reason || "rights constraint"}</div>
      )}
      <div className="mt-2 space-y-1">
        {result.alternatives.filter((alternative) => !alternative.selected).map((alternative) => (
          <div key={alternative.mode} className={alternative.permitted ? "text-muted" : "text-warn"}>
            {STANDARD_MODES.find(({ value }) => value === alternative.mode)?.label}: {alternative.reason}{alternative.failed_gates.length ? ` (${alternative.failed_gates.join(", ")})` : ""}
          </div>
        ))}
      </div>
    </div>
  );
}

function LanguageProfilePicker({
  profiles, loading, error, defaultLanguage, defaultLabel,
  selectedProfileIds, onDefaultLanguage, onToggleProfile,
}: {
  profiles: TranslationProfile[];
  loading: boolean;
  error: unknown;
  defaultLanguage: string;
  defaultLabel?: string;
  selectedProfileIds: string[];
  onDefaultLanguage: (language: string) => void;
  onToggleProfile: (profileId: string) => void;
}) {
  const languages = Array.from(new Set([
    "en",
    ...profiles.flatMap((profile) => profile.languages),
  ]));
  const languageNames: Record<string, string> = {
    en: "English (eng)", fr: "French (fra)", de: "German (deu)",
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-[7px] border border-border bg-panel/60 p-3">
        <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-text">
          <Languages size={14} className="text-accent" /> Default label language
        </label>
        <select
          className="input max-w-sm"
          value={defaultLanguage}
          onChange={(event) => onDefaultLanguage(event.target.value)}
        >
          {languages.map((language) => (
            <option key={language} value={language}>
              {languageNames[language] ?? language}{language === defaultLanguage ? " - default" : ""}
            </option>
          ))}
        </select>
        <div className="mt-1.5 text-[11px] text-muted">
          {defaultLabel ?? `${languageNames[defaultLanguage] ?? defaultLanguage} - default`}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted"><Loader2 className="mr-1 inline animate-spin" size={14} /> Loading approved translation profiles…</div>
      ) : error ? (
        <ErrorBox error={error} />
      ) : profiles.length === 0 ? (
        <EmptyState
          title="No approved translation profiles yet."
          hint="English remains the visible default. Staff can curate and approve reusable SME translations in Ontology."
        />
      ) : (
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
            Include approved SME translations
          </div>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {profiles.map((profile) => {
              const selected = selectedProfileIds.includes(profile.id);
              return (
                <li key={profile.id}>
                  <button
                    type="button"
                    onClick={() => onToggleProfile(profile.id)}
                    className={`flex h-full w-full flex-col items-start gap-2 rounded-[7px] border p-3 text-left text-xs ${selected ? "border-accent bg-accent/10" : "border-border bg-panel/60 hover:border-accent/40"}`}
                  >
                    <div className="flex w-full items-start justify-between gap-2">
                      <span className="font-semibold text-text">{profile.name}</span>
                      {selected && <CheckCircle2 size={16} className="shrink-0 text-accent" />}
                    </div>
                    <span className="text-[11px] text-muted">{profile.description}</span>
                    <div className="mt-auto flex flex-wrap gap-1">
                      <span className="pill">{profile.default_language_display}</span>
                      {profile.languages.filter((language) => language !== profile.default_language).map((language) => (
                        <span key={language} className="pill">{languageNames[language] ?? language}</span>
                      ))}
                      <span className="pill">{profile.terms.length} terms</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function PreviewPanel({
  result, pending, error, sessionId, selections, bundleName, installedStarter,
  defaultLanguage, translationProfileIds, termSetVersionIds, runtimeTarget, onRuntimeTarget, existingReviewVersionId,
  agentTemplates, selectedAgentTemplateIds, onToggleAgentTemplate,
}: {
  result: PreviewResult | null;
  pending: boolean;
  error: unknown;
  sessionId: string | null;
  selections: Record<string, PackCandidate[]>;
  bundleName: string;
  installedStarter?: string;
  defaultLanguage: string;
  translationProfileIds: string[];
  termSetVersionIds: string[];
  runtimeTarget: RuntimeTarget;
  onRuntimeTarget: (value: RuntimeTarget) => void;
  existingReviewVersionId?: string;
  agentTemplates: AgentTemplate[];
  selectedAgentTemplateIds: string[];
  onToggleAgentTemplate: (templateId: string) => void;
}) {
  if (pending) {
    return <div className="mt-4 text-sm text-muted"><Loader2 className="mr-1 inline animate-spin" size={14} /> Composing preview…</div>;
  }
  if (error) return <div className="mt-4"><ErrorBox error={error} /></div>;
  if (!result && installedStarter) {
    return (
      <div className="mt-4 rounded-[7px] border border-good/30 bg-good/10 p-4 text-sm">
        <div className="flex items-center gap-2 font-semibold text-good">
          <CheckCircle2 size={16} /> {installedStarter} is ready
        </div>
        <p className="mt-1 text-xs text-muted">
          The starter has already materialized its ontology, corpus, retrieval profile,
          agents, evals, and pipeline in this workspace.
        </p>
        <Link to="/ontology" className="btn mt-3 inline-flex">Review ontology</Link>
      </div>
    );
  }
  if (!result) return null;

  const ok = result.status === "PREVIEW_OK";
  return (
    <div className="mt-4 space-y-4">
      {result.term_set_attachment && result.term_set_attachment.term_set_version_ids.length > 0 && <p className="text-xs text-muted" role="status">
        {result.term_set_attachment.entity_count} approved entities and {result.term_set_attachment.property_count} properties will attach when the review draft is created. This Turtle preview shows the selected packs.
      </p>}
      <div className="flex items-center gap-2">
        <StatusPill status={ok ? "succeeded" : "failed"} />
        {!ok && <span className="text-sm text-bad">{result.error_stage}: {result.error_message}</span>}
      </div>

      {result.resolved_packs.length > 0 && (
        <div className="card">
          <TierStack
            segments={resolvedPacksToSegments(result.resolved_packs)}
            note="Universal modules (party, geography, financial, regulatory and risk cores) are the exact same packs every industry composes against \u2014 enforced by an automated industry-purity gate, not asserted."
          />
          <div className="hairline-t mt-3 pt-3">
            <MicroLabel className="mb-1">Resolved packs</MicroLabel>
            <div className="flex flex-wrap gap-1">
              {result.resolved_packs.map((p) => (
                <span key={`${p.name}@${p.semver}`} className="pill">{p.name}@{p.semver}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {result.validation.shacl && (
        <div className="flex items-center gap-2 text-sm">
          {result.validation.shacl.conforms
            ? <ShieldCheck size={16} className="text-good" />
            : <ShieldAlert size={16} className="text-bad" />}
          SHACL: {result.validation.shacl.conforms ? "conforms" : `${result.validation.shacl.violation_count} violation(s)`}
        </div>
      )}
      {result.validation.owl_rl && (
        <div className="flex items-center gap-2 text-sm">
          {result.validation.owl_rl.is_consistent
            ? <ShieldCheck size={16} className="text-good" />
            : <ShieldAlert size={16} className="text-bad" />}
          OWL-RL: {result.validation.owl_rl.is_consistent ? "consistent" : "inconsistent"}
        </div>
      )}
      {result.validation.industry_purity && (
        <div className="flex items-center gap-2 text-sm">
          {result.validation.industry_purity.is_pure
            ? <ShieldCheck size={16} className="text-good" />
            : <ShieldAlert size={16} className="text-bad" />}
          Industry purity: {result.validation.industry_purity.is_pure
            ? "pure" : `${result.validation.industry_purity.violations.length} violation(s)`}
        </div>
      )}

      {ok && result.acceptance && (
        <div className="card space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted">Acceptance package</div>
              <div className="mt-1 text-sm font-semibold text-text">
                {result.acceptance.status.production_approved ? "Approved for controlled release" : "Customer actions still required"}
              </div>
            </div>
            <StatusPill status={result.acceptance.status.production_approved ? "succeeded" : "running"} />
          </div>
          <div className="grid gap-2 text-xs text-muted sm:grid-cols-3">
            <div>Resolved packs: <span className="text-text">{result.acceptance.counts.resolved_packs}</span></div>
            <div>Pending actions: <span className="text-text">{result.acceptance.counts.pending_actions}</span></div>
            <div>Languages: <span className="text-text">{result.acceptance.translations.languages.join(", ")}</span></div>
          </div>
          {result.acceptance.required_actions.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Required actions</div>
              <ul className="space-y-1 text-xs text-muted">
                {result.acceptance.required_actions.map((action) => (
                  <li key={action.code} className="rounded-[7px] border border-border bg-panel/60 px-3 py-2">
                    <span className="font-semibold text-text">{action.owner}</span>: {action.summary}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.executive_summary_markdown && (
            <details className="rounded-[7px] border border-border bg-panel/40 p-3 text-xs">
              <summary className="cursor-pointer font-semibold text-text">Executive summary preview</summary>
              <pre className="mt-3 whitespace-pre-wrap font-mono text-[11px] text-muted">{result.executive_summary_markdown}</pre>
            </details>
          )}
        </div>
      )}

      {ok && result.ontology_ttl && (
        <div>
          <div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted">
            <Sparkles size={12} /> Composed ontology (Turtle, preview only — not saved)
          </div>
          <pre className="max-h-80 overflow-auto rounded-md border border-border bg-panel p-3 text-xs">
            {result.ontology_ttl}
          </pre>
        </div>
      )}

      {ok && sessionId && (
        <DiffPanel
          sessionId={sessionId}
          selections={selections}
          defaultLanguage={defaultLanguage}
          translationProfileIds={translationProfileIds}
        />
      )}

      {ok && sessionId && (
        <ScaffoldDeliverable
          sessionId={sessionId}
          selections={selections}
          bundleName={bundleName}
          defaultLanguage={defaultLanguage}
          translationProfileIds={translationProfileIds}
          termSetVersionIds={termSetVersionIds}
          runtimeTarget={runtimeTarget}
          onRuntimeTarget={onRuntimeTarget}
          existingReviewVersionId={existingReviewVersionId}
          agentTemplateIds={selectedAgentTemplateIds}
        />
      )}

      {ok && (
        <AgentTemplatePicker
          templates={agentTemplates}
          selectedTemplateIds={selectedAgentTemplateIds}
          onToggle={onToggleAgentTemplate}
        />
      )}
    </div>
  );
}

/** Enterprise-composer *deliverable* row shown on the preview step.
 *
 * The old preview panel dead-ended at a `<pre>` block of the composed
 * Turtle — that is not what a paying customer with the
 * `enterprise_scaffold` entitlement buys. This row calls
 * `POST /studio/sessions/{id}/preview-scaffold/` which materialises the
 * full project scaffold (orb.yaml + ontologies/ + shapes/ + docs/ +
 * samples/instances.ttl + mappings/README.md + evals/README.md +
 * README.md) and streams the zip back for download.
 *
 * Requires the workspace to carry the `enterprise_scaffold` entitlement;
 * staff bypass server-side. On HTTP 402 the CTA surfaces a plain
 * upgrade hint rather than a stack trace.
 */
function ScaffoldDeliverable({
  sessionId, selections, bundleName, defaultLanguage, translationProfileIds, termSetVersionIds,
  runtimeTarget, onRuntimeTarget, existingReviewVersionId, agentTemplateIds,
}: {
  sessionId: string;
  selections: Record<string, PackCandidate[]>;
  bundleName: string;
  defaultLanguage: string;
  translationProfileIds: string[];
  termSetVersionIds: string[];
  runtimeTarget: RuntimeTarget;
  onRuntimeTarget: (value: RuntimeTarget) => void;
  existingReviewVersionId?: string;
  agentTemplateIds: string[];
}) {
  const ws = useApp((s) => s.workspaceId);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [agentMsg, setAgentMsg] = useState<string | null>(null);
  const [materializedAgents, setMaterializedAgents] = useState<MaterializedAgentSummary[]>([]);
  const [reviewDraft, setReviewDraft] = useState<null | {
    version_id: string; semver: string; draft_node_count: number;
  }>(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [agentBusy, setAgentBusy] = useState(false);

  async function download() {
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      const packSelections = Object.values(selections)
        .flat()
        .reduce<{ name: string; semver: string }[]>((acc, p) => {
          if (!acc.some((x) => x.name === p.name && x.semver === p.semver)) {
            acc.push({ name: p.name, semver: p.semver });
          }
          return acc;
        }, []);
      const headers = new Headers({ "Content-Type": "application/json" });
      const r = await rawResponse(`/studio/sessions/${sessionId}/preview-scaffold/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          pack_selections: packSelections,
          bundle_slug: bundleName || "composer-preview",
          bundle_name: bundleName || "Composer preview scaffold",
          include_shapes: true,
          default_language: defaultLanguage,
          translation_profiles: translationProfileIds,
          runtime_target: runtimeTarget,
          agent_templates: agentTemplateIds,
        }),
      });
      const cd = r.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const name = m ? m[1] : `${bundleName || "composer"}-scaffold.zip`;
      const acceptance = r.headers.get("X-Orb-Acceptance") || "0";
      const docs = r.headers.get("X-Orb-Docs") || "0";
      const samples = r.headers.get("X-Orb-Samples") || "0";
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMsg(`Scaffold ready: ${docs} doc file(s), ${samples} sample file(s), ${acceptance} acceptance artifact(s), plus orb.yaml, shapes, mappings, evals stubs, and implementation guides.`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function createReviewDraft() {
    setReviewBusy(true);
    setErr(null);
    try {
      const result = await rawFetch<{ version_id: string; semver: string; draft_node_count: number }>(
        `/studio/sessions/${sessionId}/materialize-review/`,
        {
          method: "POST",
          body: JSON.stringify({
            pack_selections: selectedPacks(selections),
            default_language: defaultLanguage,
            translation_profiles: translationProfileIds,
            term_set_version_ids: termSetVersionIds,
          }),
        },
      );
      setReviewDraft(result);
    } catch (error) {
      setErr(error instanceof Error ? error.message : "Could not create review draft");
    } finally {
      setReviewBusy(false);
    }
  }

  const reviewVersionId = reviewDraft?.version_id ?? existingReviewVersionId;
  const reviewVersionQ = useQuery<OntologyVersionSummary>({
    queryKey: ["composer-review-version", reviewVersionId, ws],
    queryFn: () => rawFetch(`/ontology/versions/${reviewVersionId}/?workspace=${ws}`),
    enabled: !!reviewVersionId && !!ws,
  });
  const canMaterializeAgents = agentTemplateIds.length > 0 && reviewVersionQ.data?.status === "published";

  async function materializeAgents() {
    setAgentBusy(true);
    setAgentMsg(null);
    setMaterializedAgents([]);
    setErr(null);
    try {
      const result = await rawFetch<{ count: number; created_agents: MaterializedAgentSummary[]; published_ontology_semver: string }>(
        `/studio/sessions/${sessionId}/materialize-agent-templates/`,
        {
          method: "POST",
          body: JSON.stringify({ agent_templates: agentTemplateIds }),
        },
      );
      setMaterializedAgents(result.created_agents);
      setAgentMsg(
        `Created ${result.count} disabled agent${result.count === 1 ? "" : "s"} bound to ontology ${result.published_ontology_semver}: ${result.created_agents.map((agent) => agent.name).join(", ")}.`,
      );
    } catch (error) {
      setErr(error instanceof Error ? error.message : "Could not materialize selected agents");
    } finally {
      setAgentBusy(false);
    }
  }

  return (
    <div className="rounded-[10px] border border-accent/30 bg-accent/[0.06] p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-accent">
            <PackagePlus size={12} />
            Enterprise deliverable
          </div>
          <div className="text-[13.5px] font-semibold text-text">
            Export the complete review scaffold, including authored DRAFT markers.
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-muted">
            One zip containing <code className="font-mono text-[11px]">orb.yaml</code>,{" "}
            <code className="font-mono text-[11px]">ontologies/</code>,{" "}
            <code className="font-mono text-[11px]">shapes/</code>,{" "}
            <code className="font-mono text-[11px]">docs/</code> (Markdown per class + property),{" "}
            <code className="font-mono text-[11px]">samples/instances.ttl</code>,{" "}
            <code className="font-mono text-[11px]">mappings/</code> + <code className="font-mono text-[11px]">evals/</code> stubs,
            <code className="font-mono text-[11px]">agents/</code> scaffolds for the templates you select here,
            <code className="font-mono text-[11px]">EXECUTIVE_SUMMARY.md</code>, <code className="font-mono text-[11px]">ACCEPTANCE.json</code>,
            <code className="font-mono text-[11px]">DRAFT_REVIEW.json</code>, and a top-level README.
            This is complete as a review deliverable; production publication still requires named SME sign-off and release checks.
          </p>
          <div className="mt-3 max-w-md">
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted">
              Runtime target
            </label>
            <select
              className="input text-xs"
              value={runtimeTarget}
              onChange={(event) => onRuntimeTarget(event.target.value as RuntimeTarget)}
            >
              {RUNTIME_TARGET_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <div className="mt-1 text-[11px] text-muted">
              {RUNTIME_TARGET_OPTIONS.find((option) => option.value === runtimeTarget)?.hint}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-2">
        <button type="button" className="btn-primary" onClick={download} disabled={busy}>
          {busy ? (
            <>
              <Loader2 size={14} className="mr-1 inline animate-spin" />
              Generating…
            </>
          ) : (
            <>
              <Download size={14} className="mr-1 inline" />
              Download review scaffold
            </>
          )}
        </button>
        {!reviewVersionId ? (
          <button type="button" className="btn" onClick={createReviewDraft} disabled={reviewBusy}>
            {reviewBusy ? <Loader2 size={14} className="mr-1 inline animate-spin" /> : <ShieldCheck size={14} className="mr-1 inline" />}
            Create SME review draft
          </button>
        ) : (
          <Link
            to="/ontology"
            className="btn text-center"
            onClick={() => sessionStorage.setItem("ckos.ontology.openValidate", reviewVersionId)}
          >
            Open review draft in Ontology
          </Link>
        )}
        {agentTemplateIds.length > 0 && (
          <button
            type="button"
            className="btn"
            onClick={materializeAgents}
            disabled={!canMaterializeAgents || agentBusy}
            title={canMaterializeAgents ? "Create the selected guarded agents in this workspace" : "Publish the review draft in Ontology first"}
          >
            {agentBusy ? <Loader2 size={14} className="mr-1 inline animate-spin" /> : <Sparkles size={14} className="mr-1 inline" />}
            Materialize selected agents
          </button>
        )}
        </div>
      </div>
      {reviewDraft && <div className="mt-2 text-[11.5px] text-good">Created editable DRAFT {reviewDraft.semver}. {reviewDraft.draft_node_count} authored node(s) remain in the SME review ledger.</div>}
      <div className="mt-2 text-[11px] leading-relaxed text-muted">
        Resolve authored drafts in-platform: create the review draft here, then complete the named review in Ontology before publication.
      </div>
      {agentTemplateIds.length > 0 && !canMaterializeAgents && (
        <div className="mt-2 text-[11px] leading-relaxed text-muted">
          Publish the review draft in Ontology to enable one-click materialization of the selected guarded agent templates.
        </div>
      )}
      {msg && <div className="mt-2 text-[11.5px] text-good">{msg}</div>}
      {agentMsg && <div className="mt-2 text-[11.5px] text-good">{agentMsg}</div>}
      {materializedAgents.length > 0 && (
        <div className="mt-2 rounded-[7px] border border-good/30 bg-good/10 px-3 py-2 text-[11.5px]">
          <div className="font-semibold text-good">Review created agents</div>
          <div className="mt-1 flex flex-wrap gap-2">
            {materializedAgents.map((agent) => (
              <a
                key={agent.id}
                href={`/agents?agent=${encodeURIComponent(agent.id)}`}
                className="inline-flex items-center rounded-[5px] border border-good/30 bg-bg/60 px-2 py-1 text-text hover:border-good hover:text-good"
              >
                {agent.name}
              </a>
            ))}
          </div>
          <div className="mt-1 text-muted">
            Each agent was created disabled. Open it in Agents, review the definition, then activate it when ready.
          </div>
        </div>
      )}
      {err && <div className="mt-2 text-[11.5px] text-bad">{err}</div>}
    </div>
  );
}

/** Phase 6.5 + 7.2 (bnode-safe) — side-by-side diff of the composed
 * draft vs. the workspace's currently-published ontology.
 *
 * Backed by `POST /studio/sessions/{id}/preview-diff/` which runs the
 * same `preview_composition()` we already fetched, exports the active
 * published ontology to Turtle, and computes a bnode-isomorphism-aware
 * diff via `apps.ontology.rdf_diff.diff_ttl()`. Two structurally-
 * identical graphs whose only difference is bnode label allocation
 * therefore show zero net change — the phantom-diff bug that ships
 * in the earlier `apps.packs.diff` set-difference implementation.
 *
 * UI: collapsed by default (the raw diff is verbose). Clicking
 * "Compare against published" fetches once and renders four buckets:
 * label edits, added terms (per kind), removed terms (per kind), and
 * raw triple counts.
 */
function DiffPanel({
  sessionId, selections, defaultLanguage, translationProfileIds,
}: {
  sessionId: string;
  selections: Record<string, PackCandidate[]>;
  defaultLanguage: string;
  translationProfileIds: string[];
}) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [data, setData] = React.useState<null | {
    baseline: { semver: string | null; has_content: boolean };
    candidate: { pack_count: number };
    diff: {
      added_triples: number;
      removed_triples: number;
      unchanged_triples: number;
      added_terms: Record<string, string[]>;
      removed_terms: Record<string, string[]>;
      label_changes: Array<{ iri: string; language: string; before: string; after: string }>;
      parse_error: string | null;
    };
  }>(null);

  const run = async () => {
    setBusy(true);
    setErr(null);
    const packSelections = Object.values(selections)
      .flat()
      .map((p) => ({ name: p.name, semver: p.semver }));
    try {
      const resp = await rawResponse(`/studio/sessions/${sessionId}/preview-diff/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pack_selections: packSelections,
          default_language: defaultLanguage,
          translation_profiles: translationProfileIds,
        }),
      });
      setData(await resp.json());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "diff request failed");
    } finally {
      setBusy(false);
    }
  };

  const toggle = () => {
    if (!open && !data && !busy) run();
    setOpen((v) => !v);
  };

  return (
    <div className="rounded-[10px] border border-border bg-panel/60 p-3 text-xs">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="flex items-center gap-2 font-semibold uppercase tracking-wide text-muted">
          <Sparkles size={12} />
          Compare against published ontology
        </span>
        <span className="text-[11px] text-muted">
          {busy ? "…" : open ? "Hide" : "Show"}
        </span>
      </button>

      {open && err && (
        <div className="mt-2 text-[11.5px] text-bad">Diff failed: {err}</div>
      )}

      {open && data && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap gap-3 text-[11px] text-muted">
            <span>
              Baseline:{" "}
              {data.baseline.semver
                ? <span className="text-text">{data.baseline.semver}</span>
                : <span className="text-warn">no published version</span>}
            </span>
            <span>Candidate: <span className="text-text">{data.candidate.pack_count} packs</span></span>
            <span>Unchanged: <span className="text-good">{data.diff.unchanged_triples}</span></span>
            <span>Added: <span className="text-good">{data.diff.added_triples}</span></span>
            <span>Removed: <span className="text-bad">{data.diff.removed_triples}</span></span>
          </div>

          {data.diff.parse_error && (
            <div className="text-[11.5px] text-bad">
              Parse error: {data.diff.parse_error}
            </div>
          )}

          {data.diff.label_changes.length > 0 && (
            <div>
              <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted">
                Label changes ({data.diff.label_changes.length})
              </div>
              <ul className="space-y-1">
                {data.diff.label_changes.slice(0, 20).map((c) => (
                  <li key={`${c.iri}:${c.language}`} className="rounded border border-border bg-bg/50 px-2 py-1">
                    <div className="truncate font-mono text-[10.5px] text-muted">{c.iri}@{c.language || "\u2205"}</div>
                    <div className="flex items-center gap-2 text-[11.5px]">
                      <span className="rounded bg-bad/10 px-1 text-bad">{c.before}</span>
                      <span className="text-muted">→</span>
                      <span className="rounded bg-good/10 px-1 text-good">{c.after}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <TermBucket
              label="Added"
              tone="good"
              terms={data.diff.added_terms}
            />
            <TermBucket
              label="Removed"
              tone="bad"
              terms={data.diff.removed_terms}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function TermBucket({
  label, tone, terms,
}: {
  label: string;
  tone: "good" | "bad";
  terms: Record<string, string[]>;
}) {
  const kinds = Object.entries(terms);
  if (kinds.length === 0) {
    return (
      <div className="rounded border border-border bg-bg/50 p-2 text-[11px] text-muted">
        {label}: none
      </div>
    );
  }
  const toneClass = tone === "good" ? "text-good" : "text-bad";
  return (
    <div className="rounded border border-border bg-bg/50 p-2">
      <div className={`mb-1 text-[10.5px] font-semibold uppercase tracking-wide ${toneClass}`}>
        {label}
      </div>
      <ul className="space-y-2">
        {kinds.map(([kind, iris]) => (
          <li key={kind}>
            <div className="text-[10.5px] text-muted">{kind.replace("_", " ")} ({iris.length})</div>
            <ul className="mt-0.5 space-y-0.5">
              {iris.slice(0, 8).map((iri) => (
                <li key={iri} className="truncate font-mono text-[10.5px]">{iri}</li>
              ))}
              {iris.length > 8 && (
                <li className="text-[10px] text-muted">+ {iris.length - 8} more</li>
              )}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MarketplaceStarterPicker({
  starters, loading, error, installing, activeSlug, onInstall, onUse,
}: {
  starters: MarketplaceStarter[];
  loading: boolean;
  error: unknown;
  installing?: string;
  activeSlug: string | null;
  onInstall: (slug: string) => void;
  onUse: (starter: MarketplaceStarter) => void;
}) {
  if (loading) {
    return <div className="mt-4 text-sm text-muted"><Loader2 className="mr-1 inline animate-spin" size={14} /> Loading Marketplace starters…</div>;
  }
  if (error) return <div className="mt-4"><ErrorBox error={error} /></div>;
  return (
    <div className="mt-4">
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">
        Start from a working industry solution
      </div>
      <ul className="grid gap-3 sm:grid-cols-2">
        {starters.filter((starter) => starter.status === "stable").map((starter) => (
          <li key={starter.slug} className="flex flex-col rounded-[7px] border border-border bg-panel/60 p-3">
            <div className="text-sm font-semibold text-text">{starter.title}</div>
            <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-muted">{starter.tagline}</p>
            <div className="mt-3 flex flex-wrap gap-1 text-[10px] text-muted">
              <span className="pill">{starter.entities} entities</span>
              <span className="pill">{starter.relations} relations</span>
              <span className="pill">{starter.agents} agents</span>
            </div>
            <button
              type="button"
              className={starter.installed ? "btn mt-3" : "btn-primary mt-3"}
              disabled={activeSlug === starter.slug || installing != null}
              onClick={() => starter.installed ? onUse(starter) : onInstall(starter.slug)}
            >
              {installing === starter.slug ? (
                <><Loader2 size={13} className="mr-1 inline animate-spin" /> Installing…</>
              ) : activeSlug === starter.slug ? (
                <><CheckCircle2 size={13} className="mr-1 inline" /> Selected</>
              ) : starter.installed ? (
                <><CheckCircle2 size={13} className="mr-1 inline" /> Use starter</>
              ) : (
                <><PackagePlus size={13} className="mr-1 inline" /> Install starter</>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Track A.3 — advisory tiles for empty steps whose emptiness has a
 * per-item explanation (e.g. FIBO/FHIR/ACORD standards). Renders one
 * tile per advisory with an availability badge, provider, description
 * and a collapsible license notice. Does NOT let the user select the
 * item — advisories are informational and navigation remains in the
 * wizard's standard Back/Next controls. */
function AdvisoryTiles({ advisories }: { advisories: ComposerStepAdvisory[] }) {
  return (
    <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {advisories.map((a) => {
        const badge = availabilityBadge(a.availability);
        return (
          <li key={a.id}>
            <div className="flex h-full flex-col gap-2 rounded-[7px] border border-border bg-panel/60 p-3 text-xs">
              <div className="flex w-full items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-semibold text-text">{a.name}</div>
                  <div className="mt-0.5 text-[10.5px] uppercase tracking-[0.03em] text-muted">
                    {a.provider}
                  </div>
                </div>
                <span
                  className={`shrink-0 rounded-[5px] border px-1.5 py-[1px] font-mono text-[10px] uppercase tracking-[0.03em] ${badge.className}`}
                >
                  {badge.label}
                </span>
              </div>
              <div className="line-clamp-3 text-[11.5px] leading-snug text-muted">
                {a.description}
              </div>
              {a.how_to_clear && (
                <div className="rounded-[7px] border border-accent/20 bg-accent/5 px-2.5 py-2 text-[11px] leading-relaxed text-text">
                  {a.how_to_clear}
                </div>
              )}
              {a.drafts && a.drafts.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {a.drafts.map((draft) => <span key={draft} className="pill">{draft}</span>)}
                </div>
              )}
              <div className="mt-auto flex flex-col gap-1.5">
                <div className="text-[10.5px] uppercase tracking-[0.03em] text-muted">
                  License
                </div>
                <div className="text-[11.5px] leading-snug text-text">{a.license}</div>
                {a.license_notice && (
                  <details className="mt-1 text-[11px] text-muted">
                    <summary className="cursor-pointer text-[10.5px] uppercase tracking-[0.03em] text-muted hover:text-text">
                      Legal notice
                    </summary>
                    <p className="mt-1 leading-relaxed">{a.license_notice}</p>
                  </details>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function availabilityBadge(
  availability: ComposerStepAdvisory["availability"],
): { label: string; className: string } {
  switch (availability) {
    case "planned":
      return {
        label: "Planned",
        className: "border-accent/30 bg-accent/10 text-accent",
      };
    case "planned_structure_only":
      return {
        label: "Planned (structure)",
        className: "border-accent/30 bg-accent/10 text-accent",
      };
    case "license_gated":
      return {
        label: "License required",
        className: "border-warn/30 bg-warn/10 text-warn",
      };
    case "skeleton_paused":
      return {
        label: "SME review pending",
        className: "border-border bg-bg/50 text-muted",
      };
    case "draft_review_required":
      return {
        label: "Review in Ontology",
        className: "border-warn/30 bg-warn/10 text-warn",
      };
    default:
      return { label: "\u2014", className: "border-border bg-bg/50 text-muted" };
  }
}

function recommendedAgentTemplates(templates: AgentTemplate[], industry?: string): AgentTemplate[] {
  const normalized = (industry || "").trim().toLowerCase();
  const compatible = new Set<string>(["generic"]);
  if (normalized) compatible.add(normalized);
  if (normalized === "insurance" || normalized === "banking") compatible.add("finance");
  if (normalized === "telecom" || normalized === "media") compatible.add("saas");
  return [...templates].sort((left, right) => {
    const leftRank = compatible.has(left.industry) ? 0 : 1;
    const rightRank = compatible.has(right.industry) ? 0 : 1;
    return leftRank - rightRank || left.display_name.localeCompare(right.display_name);
  });
}

function AgentTemplatePicker({
  templates,
  selectedTemplateIds,
  onToggle,
}: {
  templates: AgentTemplate[];
  selectedTemplateIds: string[];
  onToggle: (templateId: string) => void;
}) {
  if (templates.length === 0) return null;
  return (
    <div className="card space-y-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-muted">Optional agent scaffolds</div>
        <div className="mt-1 text-sm font-semibold text-text">Add guarded agent templates to the deliverable only when the user needs them.</div>
        <p className="mt-1 text-xs leading-relaxed text-muted">
          Exports template specs only. Agent creation stays explicit.
        </p>
      </div>
      <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {templates.map((template) => {
          const selected = selectedTemplateIds.includes(template.id);
          return (
            <li key={template.id}>
              <button
                type="button"
                onClick={() => onToggle(template.id)}
                className={`flex h-full w-full flex-col items-start gap-2 rounded-[7px] border p-3 text-left text-xs ${selected ? "border-accent bg-accent/10" : "border-border bg-panel/60 hover:border-accent/40"}`}
              >
                <div className="flex w-full items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-[13px] font-semibold text-text">
                      <span>{template.display_name}</span>
                      <span
                        className="text-muted"
                        title={template.description}
                        aria-label={`${template.display_name} description`}
                      >
                        <CircleHelp size={13} />
                      </span>
                    </div>
                    <div className="mt-0.5 text-[10.5px] uppercase tracking-[0.03em] text-muted">{template.industry}</div>
                  </div>
                  {selected && <CheckCircle2 size={16} className="shrink-0 text-accent" />}
                </div>
                <div className="mt-auto flex flex-wrap gap-1">
                  <span className="pill">{template.default_model}</span>
                  {template.default_tools.map((tool) => <span key={tool.key} className="pill">{tool.key}</span>)}
                  {template.installed_count ? <span className="pill">installed {template.installed_count}</span> : null}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Buckets a real preview response's resolved packs into the two bars
 * TierStack understands: the shared universal substrate (Tier 0 / 0.5,
 * `universal: true`) vs. what was authored specifically for this industry.
 * Driven entirely by live API data — nothing illustrative here. */
function resolvedPacksToSegments(
  packs: { name: string; semver: string; tier: number; universal: boolean; kind: string }[],
): TierSegment[] {
  const universal = packs.filter((p) => p.universal);
  const scoped = packs.filter((p) => !p.universal);
  const segments: TierSegment[] = [];
  if (universal.length) {
    segments.push({
      key: "universal",
      label: "Foundation & Universal Entities",
      sublabel: universal.map((p) => p.name).join(", "),
      count: universal.length,
      tone: "universal",
    });
  }
  if (scoped.length) {
    segments.push({
      key: "industry",
      label: "Authored for this composition",
      sublabel: scoped.map((p) => p.name).join(", "),
      count: scoped.length,
      tone: "industry",
    });
  }
  return segments;
}
